import * as React from 'react'
import { ReactElement, useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { makeStyles, tokens } from '@fluentui/react-components'
import { AlbumRegular } from '@fluentui/react-icons'
import MessageContext from '../../../context/MessageContext'
import { isOfficeInitialized } from '../../../helpers/outlookHelper'
import OverflowToolbarPanel, { HammockToolbarButtonData } from '../../../components/Overflow/OverflowToolbarPanel'

const useStyles = makeStyles({
    // Overflow measures this container. It must be able to shrink (minWidth 0) so that a message linked to
    // several files collapses its buttons into the overflow menu instead of pushing the right-hand footer
    // section (Support, MailboxAvatar, Version) onto a second line.
    container: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
    },
    toolbar: {
        columnGap: tokens.spacingHorizontalXS,
    },
})

/**
 * v9 replacement for the legacy (Fluent-v8 Stack/ActionButton) quicklink widget: one button per file the opened
 * message is linked to, so the add-in can jump straight to it. Rendered as an OverflowToolbarPanel: the footer is
 * a single row, so whatever does not fit moves into the overflow menu rather than wrapping.
 */
export default function FileLink(): ReactElement {
    const styles = useStyles()
    const { fileIdMap } = useContext(MessageContext)
    const history = useHistory()

    const buttons = useMemo<HammockToolbarButtonData[]>(
        () =>
            [...(fileIdMap?.entries() ?? [])].map(([reference, id]) => ({
                value: reference,
                title: reference,
                icon: <AlbumRegular />,
                onClick: () => history.push(`/file/${id}`),
            })),
        [fileIdMap, history],
    )

    if (!isOfficeInitialized() || !fileIdMap) {
        return <></>
    }

    return (
        <OverflowToolbarPanel
            containerClassName={styles.container}
            // Every button may collapse: with one file link the single button still has to give way to the
            // footer sections that are always there.
            minimumVisible={0}
            toolbarProps={{ size: 'small', className: styles.toolbar }}
            getToolbarButtonProps={button => ({
                appearance: 'subtle',
                // The reference is the button's label; OverflowToolbarPanel only renders the icon itself.
                children: button?.title,
            })}
            buttons={buttons}
            overflowButtonLabelKey={'base.item.label'}
        />
    )
}
